import type { ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useWavesurferContext from 'services/hooks/wavesurfer';
import { useAnalyticsTracking } from 'services/hooks/funnel/useAnalyticsTracking';
import usePrevious from 'services/hooks/usePrevious';
import { Funnel, locations } from 'services/routes/locations';

export type FunnelInfo = {
  funnel: Funnel;
  startedFrom: string;
};

export const FunnelContext = createContext<FunnelInfo[]>([]);

export const FunnelProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const location = useLocation();
  const { pathname } = location;

  const [currentPathname, setCurrentPathname] = useState<string>('/');
  const previousPathname: string = usePrevious(currentPathname);

  const [activeFunnels, setActiveFunnels] = useState<FunnelInfo[]>([]);
  const previousActiveFunnels: FunnelInfo[] = usePrevious(activeFunnels);

  const { trackUserStatus } = useAnalyticsTracking();

  const { pause } = useWavesurferContext();

  const onFunnelStarted = (info: FunnelInfo): void => {
    if (
      info.funnel === Funnel.Remixer ||
      info.funnel === Funnel.AIMusicGenerator ||
      info.funnel === Funnel.DISTRIBUTION ||
      info.funnel === Funnel.ACCOUNT
    ) {
      pause();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onFunnelLeft = (info: FunnelInfo): void => {};

  const includesFunnel = (info: FunnelInfo, array: FunnelInfo[]): boolean =>
    array.some((fnInfo: FunnelInfo) => fnInfo.funnel === info.funnel);

  const updateActiveFunnelsFor = (funnelPathname: string): void => {
    const funnels = locations.funnelsForPathname(funnelPathname) ?? [];

    const funnelInfos = funnels.map((funnel) => {
      const funnelIndex = activeFunnels.findIndex(
        (info) => info.funnel === funnel
      );

      if (funnelIndex >= 0) {
        return { funnel, startedFrom: activeFunnels[funnelIndex].startedFrom };
      }

      return { funnel, startedFrom: previousPathname };
    });

    setActiveFunnels(funnelInfos);
  };

  useEffect(() => {
    setCurrentPathname(pathname);
  }, [pathname]);

  useEffect(() => {
    trackUserStatus();
    updateActiveFunnelsFor(currentPathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPathname]);

  useEffect(() => {
    if (!previousActiveFunnels) return;
    activeFunnels.forEach((info: FunnelInfo) => {
      if (includesFunnel(info, activeFunnels)) {
        onFunnelStarted(info);
      }
    });
    previousActiveFunnels.forEach((info: FunnelInfo) => {
      if (!includesFunnel(info, activeFunnels)) {
        onFunnelLeft(info);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFunnels]);

  return (
    <FunnelContext.Provider value={activeFunnels}>
      {children}
    </FunnelContext.Provider>
  );
};
